import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { X, Camera, ImageIcon, Video, Globe, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import InAppCamera from "@/components/reels/InAppCamera";

// Extract a JPEG thumbnail from a video data URL (first non-black frame).
async function extractVideoThumbnail(videoDataUrl) {
  return new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.src = videoDataUrl;
    v.muted = true;
    v.playsInline = true;
    v.preload = "metadata";
    v.crossOrigin = "anonymous";
    const cleanup = () => {
      try { v.pause(); } catch {}
      v.src = "";
      v.remove();
    };
    v.onloadedmetadata = () => {
      // Seek slightly past t=0 to avoid black frame in some codecs
      const target = Math.min(0.15, Math.max(0, (v.duration || 1) * 0.05));
      try { v.currentTime = target; } catch { /* older Safari */ }
    };
    v.onseeked = () => {
      try {
        const w = v.videoWidth || 320;
        const h = v.videoHeight || 480;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(v, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.72);
        cleanup();
        resolve(dataUrl);
      } catch (e) { cleanup(); reject(e); }
    };
    v.onerror = () => { cleanup(); reject(new Error("thumb-extract-failed")); };
    // Kick off metadata load
    setTimeout(() => { try { v.load(); } catch {} }, 0);
  });
}

const MAX_CAMERA_DURATION = 8;   // seconds — in-app camera cap (product intent: quick snaps)
const MAX_GALLERY_DURATION = 30; // seconds — gallery pick cap (looser so real phone videos fit)
const MAX_BYTES = 15 * 1024 * 1024; // 15MB decoded

// Playback-side filters. The filter *key* is persisted per-reel; playback
// resolves it to a CSS filter string. Video filters are applied via CSS at
// render time (no re-encoding); image filters are baked into the JPEG at
// publish-time via a hidden canvas so remote clients see the same thing
// regardless of their browser's CSS filter support.
const FILTERS = [
  { key: "none",   label: "Orjinal", css: "none" },
  { key: "gray",   label: "Gri",     css: "grayscale(1)" },
  { key: "sepia",  label: "Sepya",   css: "sepia(0.8) saturate(1.1)" },
  { key: "bright", label: "Parlak",  css: "brightness(1.2) contrast(1.05) saturate(1.15)" },
  { key: "cool",   label: "Soğuk",   css: "contrast(1.05) hue-rotate(-15deg) saturate(1.1)" },
  { key: "warm",   label: "Sıcak",   css: "contrast(1.05) hue-rotate(15deg) saturate(1.15) brightness(1.05)" },
];
export const filterCss = (key) => (FILTERS.find((f) => f.key === key)?.css) || "none";

/** Bake a CSS filter into an image data URL via a hidden canvas. */
async function bakeImageFilter(dataUrl, css) {
  if (!css || css === "none") return dataUrl;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        try { ctx.filter = css; } catch {}
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.88));
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error("filter-bake-failed"));
    img.src = dataUrl;
  });
}

// Fallback MIME sniffing when browser reports an empty `f.type` (common on
// some Android gallery pickers). Returns "video" | "image" | null.
function guessMediaKindFromName(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || "");
  if (!m) return null;
  const ext = m[1].toLowerCase();
  if (["mp4", "webm", "mov", "m4v", "3gp", "avi", "mkv", "hevc"].includes(ext)) return "video";
  if (["jpg", "jpeg", "png", "webp", "gif", "heic", "heif", "bmp"].includes(ext)) return "image";
  return null;
}

/**
 * Two-step composer:
 * 1) pick file (image or video, camera or gallery)
 * 2) preview + set caption/visibility/mode + publish
 */
export default function ReelComposer({ onClose, onPublished }) {
  const [file, setFile] = useState(null);
  const [dataUrl, setDataUrl] = useState(null); // for preview + upload
  const [mediaType, setMediaType] = useState(null); // "image" | "video"
  const [source, setSource] = useState(null); // "camera" | "gallery" — controls duration cap
  const [duration, setDuration] = useState(null);
  const [caption, setCaption] = useState("");
  const [visibility, setVisibility] = useState("friends");
  const [mode, setMode] = useState("permanent");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // 0..1 while uploading main media, null otherwise
  const [error, setError] = useState("");
  const [previewWarning, setPreviewWarning] = useState(""); // e.g. codec not supported — informational, doesn't block upload
  const [cameraOpen, setCameraOpen] = useState(false);
  const [filter, setFilter] = useState("none");
  const [filterThumb, setFilterThumb] = useState(null); // static JPEG snapshot for filter-chip previews (mobile perf)
  const videoPreviewRef = useRef(null);

  // Whenever a new media is loaded into the composer, snapshot ONE frame
  // (for videos) or use the image itself as the source for the filter-chip
  // mini-previews. Prevents mounting 6 simultaneous <video> elements on mobile,
  // which was heavy per iter35 code review.
  useEffect(() => {
    if (!dataUrl || !mediaType) { setFilterThumb(null); return; }
    let cancelled = false;
    if (mediaType === "image") {
      setFilterThumb(dataUrl);
      return () => { cancelled = true; };
    }
    // video → capture first frame to a canvas
    const v = document.createElement("video");
    v.src = dataUrl;
    v.muted = true;
    v.playsInline = true;
    v.preload = "metadata";
    v.crossOrigin = "anonymous";
    v.onloadeddata = () => {
      if (cancelled) return;
      try {
        v.currentTime = Math.min(0.1, (v.duration || 1) * 0.02);
      } catch { /* ignore */ }
    };
    v.onseeked = () => {
      if (cancelled) return;
      try {
        const c = document.createElement("canvas");
        const targetW = 112;
        const ratio = (v.videoWidth || 720) / (v.videoHeight || 1280);
        c.width = targetW;
        c.height = Math.max(1, Math.round(targetW / ratio));
        const ctx = c.getContext("2d");
        ctx.drawImage(v, 0, 0, c.width, c.height);
        setFilterThumb(c.toDataURL("image/jpeg", 0.75));
      } catch { /* fall back to no thumb */ }
    };
    v.onerror = () => { if (!cancelled) setFilterThumb(null); };
    try { v.load(); } catch {}
    return () => {
      cancelled = true;
      try { v.pause(); } catch {}
      v.src = "";
    };
  }, [dataUrl, mediaType]);

  useEffect(() => {
    return () => { setDataUrl(null); };
  }, []);

  const onCameraCapture = ({ dataUrl: url, mediaType: mt, duration: dur }) => {
    setError("");
    setMediaType(mt);
    setSource("camera");
    setDataUrl(url);
    setDuration(dur);
    setFile(null);
    setCameraOpen(false);
  };

  const onFile = async (e) => {
    setError("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      setError(`Dosya ${Math.round(MAX_BYTES / 1024 / 1024)}MB'dan büyük olamaz (${(f.size / 1024 / 1024).toFixed(1)}MB seçildi)`);
      return;
    }
    let isVideo = f.type.startsWith("video/");
    let isImage = f.type.startsWith("image/");
    // Some pickers (Android + a handful of iOS builds) report empty `f.type`.
    // Fall back to file-extension sniffing so the user isn't blocked.
    if (!isVideo && !isImage) {
      const guess = guessMediaKindFromName(f.name);
      if (guess === "video") isVideo = true;
      else if (guess === "image") isImage = true;
    }
    if (!isVideo && !isImage) {
      setError("Sadece foto veya video yükleyebilirsin (dosya türü tanınmadı)");
      return;
    }
    setMediaType(isVideo ? "video" : "image");
    setSource("gallery");
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setDataUrl(String(reader.result));
    reader.onerror = () => setError("Dosya okunamadı, lütfen başka bir dosya deneyin");
    reader.readAsDataURL(f);
  };

  const maxDurationFor = (src) => (src === "camera" ? MAX_CAMERA_DURATION : MAX_GALLERY_DURATION);

  const onVideoLoaded = () => {
    const el = videoPreviewRef.current;
    if (!el) return;
    const d = el.duration;
    // Only trust <video>.duration if we don't already have a duration from
    // the in-app camera (MediaRecorder WebM often reports NaN here).
    if ((duration == null || Number.isNaN(duration)) && Number.isFinite(d) && d > 0) {
      setDuration(d);
    }
    const cap = maxDurationFor(source);
    if (Number.isFinite(d) && d > cap + 0.3) {
      setError(`Video ${cap} saniyeyi geçemez (mevcut: ${d.toFixed(1)}sn)`);
    }
  };

  const clear = () => {
    setFile(null);
    setDataUrl(null);
    setMediaType(null);
    setSource(null);
    setDuration(null);
    setCaption("");
    setError("");
    setPreviewWarning("");
    setFilter("none");
  };

  const publish = async () => {
    if (!dataUrl || !mediaType) return;
    const cap = maxDurationFor(source);
    if (mediaType === "video" && duration && duration > cap + 0.3) {
      setError(`Video ${cap} saniyeyi geçemez`);
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setError("");
    try {
      const css = filterCss(filter);
      // Images bake the filter into the JPEG so all clients see the same
      // colors. Videos keep the filter as metadata (CSS on playback).
      let uploadDataUrl = dataUrl;
      if (mediaType === "image" && css !== "none") {
        try { uploadDataUrl = await bakeImageFilter(dataUrl, css); } catch { /* fall back to raw */ }
      }
      // For videos, extract & upload a thumbnail so feed placeholders can
      // show a real poster while the video buffers.
      let thumbnailUrl = null;
      if (mediaType === "video") {
        try {
          const thumbDataUrl = await extractVideoThumbnail(dataUrl);
          const thumbRes = await api.post("/uploads", { data_url: thumbDataUrl, kind: "reel" });
          thumbnailUrl = thumbRes.data.url;
        } catch { /* thumbnail is best-effort; publish continues without */ }
      }
      const upl = await api.post(
        "/uploads",
        { data_url: uploadDataUrl, kind: "reel" },
        {
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            setUploadProgress(Math.min(1, evt.loaded / evt.total));
          },
        },
      );
      // Upload is done; visually confirm 100% while the metadata call finishes
      setUploadProgress(1);
      const { data: reel } = await api.post("/reels", {
        media_url: upl.data.url,
        media_type: mediaType,
        thumbnail_url: thumbnailUrl,
        caption: caption.trim() || null,
        duration: mediaType === "video" ? duration || null : null,
        visibility,
        mode,
        // Only send filter for videos — images already have it baked in.
        filter: mediaType === "video" ? (filter === "none" ? null : filter) : null,
      });
      onPublished?.(reel);
    } catch (e) {
      setError(formatApiError(e));
    }
    setUploading(false);
    setUploadProgress(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] bg-black/70 flex items-end md:items-center justify-center p-0 md:p-4"
      data-testid="reel-composer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 26, stiffness: 260 }}
        className="w-full md:max-w-lg bg-[#F9F8F6] text-[#0A0A0A] border-t-[3px] md:border-hard md:shadow-hard-lg border-[#0A0A0A] md:rounded-3xl rounded-t-3xl max-h-[95vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="p-4 md:p-6">
          <div className="w-12 h-1.5 bg-[#0A0A0A] rounded-full mx-auto mb-3 md:hidden" />

          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase font-black tracking-widest text-[#555]">Yeni Snap</div>
              <h2 className="font-display text-2xl leading-none">Paylaş</h2>
            </div>
            <button
              onClick={onClose}
              data-testid="composer-close"
              className="w-10 h-10 border-hard-2 rounded-xl flex items-center justify-center bg-white press-btn shadow-hard-sm"
              aria-label="Kapat"
            >
              <X strokeWidth={3} className="w-4 h-4" />
            </button>
          </div>

          {/* Step 1 — pick media */}
          {!dataUrl && (
            <div className="space-y-3" data-testid="composer-pick">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setCameraOpen(true)}
                  data-testid="composer-camera"
                  className="border-hard-2 shadow-hard-sm rounded-2xl bg-white p-4 flex flex-col items-center gap-2 press-btn"
                >
                  <div className="w-12 h-12 rounded-xl border-hard-2 bg-[#CCFF00] flex items-center justify-center">
                    <Camera strokeWidth={3} className="w-5 h-5" />
                  </div>
                  <div className="font-black text-sm">Kamera</div>
                  <div className="text-[11px] text-[#555] text-center">Uygulama içi kamera · 8sn video veya foto</div>
                </button>

                <label
                  data-testid="composer-gallery"
                  className="border-hard-2 shadow-hard-sm rounded-2xl bg-white p-4 flex flex-col items-center gap-2 press-btn cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl border-hard-2 bg-[#D4B3FF] flex items-center justify-center">
                    <ImageIcon strokeWidth={3} className="w-5 h-5" />
                  </div>
                  <div className="font-black text-sm">Galeri</div>
                  <div className="text-[11px] text-[#555] text-center">Cihazından seç</div>
                  <input type="file" accept="image/*,video/*" className="hidden" onChange={onFile} data-testid="composer-file-input" />
                </label>
              </div>
              <div className="text-[11px] text-[#555] text-center px-4">
                Kamera: {MAX_CAMERA_DURATION}sn · Galeri: {MAX_GALLERY_DURATION}sn&apos;ye kadar · maks. 15MB
              </div>
            </div>
          )}

          {/* Step 2 — preview + options */}
          {dataUrl && (
            <div className="space-y-4" data-testid="composer-preview">
              <div className="relative bg-[#0A0A0A] border-hard-2 rounded-2xl overflow-hidden aspect-[9/16] max-h-[45vh]">
                {mediaType === "video" ? (
                  <video
                    ref={videoPreviewRef}
                    src={dataUrl}
                    className="w-full h-full object-cover"
                    style={{ filter: filterCss(filter) }}
                    autoPlay
                    muted
                    loop
                    playsInline
                    onLoadedMetadata={(e) => {
                      onVideoLoaded();
                      // If duration/dimensions decoded but nothing paints, some
                      // browsers still fire loadedmetadata — we treat that as
                      // "playable enough to preview".
                      if (e.currentTarget?.videoWidth > 0) setPreviewWarning("");
                    }}
                    onError={() => setPreviewWarning(
                      "Bu video formatı tarayıcıda oynatılamıyor (ör. iPhone HEVC/H.265). Yine de yükleyebilirsin — feed'de destekleyen cihazlarda gösterilecek."
                    )}
                    data-testid="composer-video-preview"
                  />
                ) : (
                  <img
                    src={dataUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    style={{ filter: filterCss(filter) }}
                    onError={() => setPreviewWarning("Resim önizlenemedi — dosya bozuk olabilir")}
                    data-testid="composer-image-preview"
                  />
                )}
                <button
                  onClick={clear}
                  data-testid="composer-clear"
                  className="absolute top-2 right-2 w-9 h-9 bg-white/90 border-hard-2 rounded-xl flex items-center justify-center press-btn"
                  aria-label="Değiştir"
                >
                  <X strokeWidth={3} className="w-4 h-4" />
                </button>
                {mediaType === "video" && duration && (
                  <div className="absolute bottom-2 left-2 text-[10px] uppercase font-black tracking-widest bg-white border-hard-2 rounded-full px-2 py-0.5">
                    {duration.toFixed(1)}sn
                  </div>
                )}
              </div>

              {/* Filter picker — horizontal scroll of chips with mini previews */}
              <div>
                <div className="text-[10px] uppercase font-black tracking-widest text-[#555] mb-1.5 px-1">Filtre</div>
                <div
                  className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
                  style={{ scrollbarWidth: "none" }}
                  data-testid="composer-filter-row"
                >
                  {FILTERS.map((f) => {
                    const active = filter === f.key;
                    return (
                      <button
                        key={f.key}
                        onClick={() => setFilter(f.key)}
                        data-testid={`composer-filter-${f.key}`}
                        className={`shrink-0 border-hard-2 rounded-xl overflow-hidden press-btn ${active ? "shadow-hard-sm" : ""}`}
                      >
                        <div className="w-14 h-14 bg-[#0A0A0A] relative overflow-hidden">
                          {filterThumb ? (
                            <img
                              src={filterThumb}
                              alt=""
                              className="w-full h-full object-cover"
                              style={{ filter: f.css }}
                              aria-hidden
                            />
                          ) : (
                            <div className="absolute inset-0 bg-[#111]" />
                          )}
                        </div>
                        <div className={`text-[10px] font-black uppercase tracking-widest text-center py-1 ${active ? "bg-[#CCFF00]" : "bg-white"}`}>{f.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={2}
                maxLength={140}
                placeholder="Açıklama (opsiyonel)…"
                data-testid="composer-caption"
                className="w-full bg-white border-hard rounded-xl px-3 py-2 resize-none text-[16px] focus:outline-none"
              />
              <div className="text-[10px] text-right text-[#888] -mt-2">{caption.length}/140</div>

              {/* Visibility */}
              <div>
                <div className="text-[10px] uppercase font-black tracking-widest text-[#555] mb-1">Kimler görebilir?</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setVisibility("friends")}
                    data-testid="composer-visibility-friends"
                    className={`border-hard-2 rounded-xl px-3 py-2.5 press-btn text-left ${visibility === "friends" ? "bg-[#CCFF00] shadow-hard-sm" : "bg-white"}`}
                  >
                    <Lock strokeWidth={3} className="w-4 h-4 mb-1" />
                    <div className="font-black text-sm">Sadece Arkadaşlar</div>
                    <div className="text-[10px] text-[#555]">Yalnızca ekli arkadaşların görür</div>
                  </button>
                  <button
                    onClick={() => setVisibility("public")}
                    data-testid="composer-visibility-public"
                    className={`border-hard-2 rounded-xl px-3 py-2.5 press-btn text-left ${visibility === "public" ? "bg-[#CCFF00] shadow-hard-sm" : "bg-white"}`}
                  >
                    <Globe strokeWidth={3} className="w-4 h-4 mb-1" />
                    <div className="font-black text-sm">Herkese Açık</div>
                    <div className="text-[10px] text-[#555]">Uygulama içinde herkes görür</div>
                  </button>
                </div>
              </div>

              {/* Mode */}
              <div>
                <div className="text-[10px] uppercase font-black tracking-widest text-[#555] mb-1">Görüntüleme modu</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setMode("permanent")}
                    data-testid="composer-mode-permanent"
                    className={`border-hard-2 rounded-xl px-3 py-2.5 press-btn text-left ${mode === "permanent" ? "bg-[#CCFF00] shadow-hard-sm" : "bg-white"}`}
                  >
                    <Eye strokeWidth={3} className="w-4 h-4 mb-1" />
                    <div className="font-black text-sm">Kalıcı</div>
                    <div className="text-[10px] text-[#555]">Herkes istediği kadar izleyebilir</div>
                  </button>
                  <button
                    onClick={() => setMode("once")}
                    data-testid="composer-mode-once"
                    className={`border-hard-2 rounded-xl px-3 py-2.5 press-btn text-left ${mode === "once" ? "bg-[#CCFF00] shadow-hard-sm" : "bg-white"}`}
                  >
                    <EyeOff strokeWidth={3} className="w-4 h-4 mb-1" />
                    <div className="font-black text-sm">Tek Sefer</div>
                    <div className="text-[10px] text-[#555]">İzleyen bir daha göremez</div>
                  </button>
                </div>
              </div>

              {error && <div className="border-hard-2 bg-[#FFD0C2] rounded-xl px-3 py-2 text-sm font-bold" data-testid="composer-error">{error}</div>}

              {previewWarning && !error && (
                <div
                  className="border-hard-2 bg-[#FFF3C2] rounded-xl px-3 py-2 text-xs font-bold leading-snug"
                  data-testid="composer-preview-warning"
                  role="status"
                >
                  ⚠️ {previewWarning}
                </div>
              )}

              {/* Progress bar while the main media is uploading. Shown ABOVE
                  the publish button so users see immediate feedback. */}
              {uploading && uploadProgress !== null && (
                <div data-testid="composer-progress" className="space-y-1">
                  <div className="h-2.5 w-full bg-white border-hard-2 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#CCFF00] transition-all duration-150"
                      style={{ width: `${Math.round(uploadProgress * 100)}%` }}
                      data-testid="composer-progress-bar"
                    />
                  </div>
                  <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-[#555]">
                    <span data-testid="composer-progress-pct">%{Math.round(uploadProgress * 100)}</span>
                    <span>{uploadProgress >= 1 ? "Kaydediliyor…" : "Yükleniyor…"}</span>
                  </div>
                </div>
              )}

              <button
                onClick={publish}
                disabled={uploading || (mediaType === "video" && duration && duration > maxDurationFor(source) + 0.3)}
                data-testid="composer-publish"
                className="w-full bg-[#0A0A0A] text-white font-black rounded-xl px-5 py-3 border-hard shadow-hard press-btn disabled:opacity-60"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> {uploadProgress !== null && uploadProgress < 1 ? `Yükleniyor %${Math.round(uploadProgress * 100)}` : "Kaydediliyor…"}</>
                ) : "Snap'i paylaş"}
              </button>
            </div>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {cameraOpen && (
          <InAppCamera
            onClose={() => setCameraOpen(false)}
            onCapture={onCameraCapture}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
